import React, {useState, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, SkeletonText} from '@chakra-ui/react'
import WebDrawer from '../../../../web-drawer'
import {renderLayoutsSwitch, renderComponentsSwitch} from '../../../utils/contentRenderer'
import {
    useGDIRewardTileContext,
    REWARD_TYPE_EXCLUSIVE_REWARDS
} from '../../utils/gdi-reward-tile-context'
import {REWARD_TYPE_EARN_ACTIONS} from '../../layouts/gdiRewardTileBlock/earn-action-completion'
import {
    GDIDynamicContentProvider,
    useGDIDynamicContentContext
} from '../../utils/gdi-dynamic-content-context'
import {useIsAudienceTargetMatch} from '../../utils/gdi-audience-targeting'
import useDrawerLazyContent from './use-drawer-lazy-content'

// Maps CMS Size values to WebDrawer size prop values
const SIZE_MAP = {Hug: 'hug', Min: 'min', '85vh': '85vh', Full: 'full'}

const renderArea = (contentArea) => {
    if (!Array.isArray(contentArea) || contentArea.length === 0) return null
    const nodes = contentArea
        .map((item, idx) => {
            const exp = item?.ContentLink?.Expanded
            if (!exp) return null
            return (
                <React.Fragment key={idx}>
                    {renderLayoutsSwitch(exp) || renderComponentsSwitch(exp)}
                </React.Fragment>
            )
        })
        .filter(Boolean)
    return nodes.length > 0 ? nodes : null
}

/**
 * GDICustomDrawer
 *
 * Renders a trigger element that opens a WebDrawer populated with
 * CMS-authored GDIDrawerBlock content (title, body, footer, size).
 *
 * @param {Object}   drawerBlock    - Expanded GDIDrawerBlock from CMS
 * @param {Function} renderTrigger  - ({onOpen}) => ReactNode — renders the clickable trigger
 */
const GDICustomDrawer = ({drawerBlock, renderTrigger}) => {
    const [isOpen, setIsOpen] = useState(false)
    const rewardTile = useGDIRewardTileContext()
    const audienceMatches = useIsAudienceTargetMatch(drawerBlock?.GDIDrawerBlockAudienceTargeting)
    const isHeaderlessRewardTile =
        rewardTile?.type === REWARD_TYPE_EXCLUSIVE_REWARDS ||
        rewardTile?.type === REWARD_TYPE_EARN_ACTIONS

    // Surface this drawer's onClose to descendants through
    // GDIDynamicContentProvider so interactive CMS dynamic tags rendered in
    // the body (e.g. `{newsletterForm}`) can dismiss the parent drawer on
    // successful action. Any parent dynamic-content values are forwarded so
    // outer tokens (loyalty fields, etc.) still resolve inside the drawer.
    const parentDynamicContent = useGDIDynamicContentContext()
    const drawerDynamicContent = useMemo(
        () => ({
            ...parentDynamicContent,
            closeParentModal: () => setIsOpen(false)
        }),
        [parentDynamicContent]
    )

    // Fallback for Optimizely Graph's max-expansion-depth cap: when this drawer
    // sits deep enough in the page payload that its inner content areas arrive
    // partially populated (null Expanded entries, or missing items that were
    // added in the CMS after the page query was authored), re-fetch the whole
    // drawer by its own ContentLink.Id once the drawer opens. Querying that
    // Id as a root resets the depth counter so the freshest title / body /
    // footer arrays come back.
    //
    // The lazy fetch is scoped to drawers nested under a `GDIRewardTileBlock`
    // (detected via `useGDIRewardTileContext`). Reward-tile drawers sit at
    // the deepest level of the page payload, so they are the ones that hit
    // the expansion-depth cap; other drawers don't need the runtime refetch.
    const {titleArea, bodyArea, footerArea, isLoading} = useDrawerLazyContent({
        drawerId: drawerBlock?.ContentLink?.Id,
        titleArea: drawerBlock?.GDIDrawerBlockTitle,
        bodyArea: drawerBlock?.GDIDrawerBlockContent,
        footerArea: drawerBlock?.Footer,
        enabled: isOpen && !!rewardTile
    })

    if (!drawerBlock || !renderTrigger) return null
    if (!audienceMatches) return null

    const size = SIZE_MAP[drawerBlock.Size] ?? 'hug'

    const titleContent = renderArea(titleArea)
    const bodyContent = renderArea(bodyArea)
    const footerContent = renderArea(footerArea)

    // Show a skeleton while the lazy fetch is in flight and the body hasn't
    // resolved yet, so the drawer doesn't appear empty between open and render.
    const bodyNode =
        isLoading && !bodyContent ? (
            <Box padding={4}>
                <SkeletonText noOfLines={6} spacing={4} skeletonHeight={3} />
            </Box>
        ) : (
            bodyContent
        )

    // When the drawer is opened from a reward tile whose CMS body fully
    // controls its own heading layout (Exclusive Rewards, Earn Actions),
    // suppress the WebDrawer's built-in title row and let the header float
    // inside the content area.
    const drawerTitleProps = isHeaderlessRewardTile
        ? {title: '', headerInContent: true}
        : titleContent
        ? {title: titleContent}
        : {}

    return (
        <>
            {renderTrigger({onOpen: () => setIsOpen(true)})}
            <WebDrawer
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                {...drawerTitleProps}
                size={size}
                footer={footerContent || undefined}
            >
                <GDIDynamicContentProvider value={drawerDynamicContent}>
                    {bodyNode}
                </GDIDynamicContentProvider>
            </WebDrawer>
        </>
    )
}

GDICustomDrawer.propTypes = {
    drawerBlock: PropTypes.shape({
        GDIDrawerBlockTitle: PropTypes.array,
        GDIDrawerBlockContent: PropTypes.array,
        Footer: PropTypes.array,
        Size: PropTypes.string,
        GDIDrawerBlockAudienceTargeting: PropTypes.object
    }),
    renderTrigger: PropTypes.func.isRequired
}

export default GDICustomDrawer
